import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  VmBookingLink,
  VmSchedulingSource,
  VmSettings,
  VM_DISCLOSURE,
  VM_SMS_CONFIRMATION,
  VM_SMS_OFFER,
} from '@models';
import { VoicemailStore } from '@services';
import { TooltipDirective, TooltipShellComponent } from '@ui-lib';
import { VoicemailAudioPreviewService } from './voicemail-audio-preview.service';

function httpsUrl(control: AbstractControl<string>): ValidationErrors | null {
  const value = control.value.trim();
  if (!value) return null;
  if (!/^https:\/\//i.test(value)) return { httpsUrl: true };
  try {
    const url = new URL(value);
    return url.protocol === 'https:' &&
      Boolean(url.hostname) &&
      !url.username &&
      !url.password
      ? null
      : { httpsUrl: true };
  } catch {
    return { httpsUrl: true };
  }
}

function nonBlank(control: AbstractControl<string>): ValidationErrors | null {
  return control.value.trim() ? null : { required: true };
}

type SettingsDialog =
  | 'preview'
  | 'notice'
  | 'activate'
  | 'discard'
  | 'support'
  | null;

/** How the resolved booking link is described, by the rule the API said won. */
const SOURCE_LABEL: Record<VmSchedulingSource, string> = {
  override: 'A different link you chose',
  'scheduling-link': 'The booking link in your Scheduling type',
  'landing-page': 'Your published iCareDx landing page',
};

const NO_LINK: VmBookingLink = { url: null, source: null };

/** Used until the server's own limit arrives, and only then. */
const DEFAULT_GREETING_LIMIT = 500;

@Component({
  selector: 'provider-portal-voicemail-settings',
  imports: [ReactiveFormsModule, TooltipDirective, TooltipShellComponent],
  templateUrl: './voicemail-settings.component.html',
  styleUrl: './voicemail-settings.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [VoicemailAudioPreviewService],
})
export class VoicemailSettingsComponent implements OnDestroy {
  readonly back = output<void>();
  readonly store = inject(VoicemailStore);
  readonly preview = inject(VoicemailAudioPreviewService);
  private readonly router = inject(Router);
  /**
   * The notice as the server speaks it, with the local copy used only until settings arrive. A
   * provider approving what callers hear has to be reading what callers hear.
   */
  readonly disclosure = computed(
    () => this.settings()?.disclosure || VM_DISCLOSURE
  );
  readonly disclosureVersion = computed(
    () => this.settings()?.disclosureVersion ?? ''
  );
  /** The text-link prompt and its confirmation as calls speak them, served like the notice. */
  readonly smsOffer = computed(() => this.settings()?.smsOffer || VM_SMS_OFFER);
  readonly smsConfirmation = computed(
    () => this.settings()?.smsConfirmation || VM_SMS_CONFIRMATION
  );
  readonly previewCall = signal<'standard' | 'booked'>('standard');
  /** The message shows a placeholder; the actual link is the card above it. */
  readonly bookingMessage = computed(() => this.settings()?.smsMessage ?? '');
  readonly form = new FormGroup({
    greeting: new FormControl('', {
      nonNullable: true,
      validators: [nonBlank, Validators.maxLength(DEFAULT_GREETING_LIMIT)],
    }),
    overrideEnabled: new FormControl(false, { nonNullable: true }),
    overrideUrl: new FormControl('', {
      nonNullable: true,
      validators: [httpsUrl],
    }),
  });
  readonly draftGreeting = signal('');

  /**
   * Keeps the form's limit on the server's. Hard-coding it means the field accepts a greeting the
   * API then rejects, and the provider loses what they typed to a validation error they were never
   * shown.
   */
  private readonly greetingLimit = effect(() => {
    const limit = this.settings()?.maxGreetingLength;

    if (!limit || limit === this.appliedGreetingLimit) return;

    this.appliedGreetingLimit = limit;
    this.form.controls.greeting.setValidators([
      nonBlank,
      Validators.maxLength(limit),
    ]);
    this.form.controls.greeting.updateValueAndValidity({ emitEvent: false });
  });

  private appliedGreetingLimit = DEFAULT_GREETING_LIMIT;
  readonly draftOverrideEnabled = signal(false);
  readonly draftOverrideUrl = signal('');
  readonly dirty = signal(false);
  readonly submitted = signal(false);
  readonly saved = signal(false);
  readonly copied = signal(false);
  readonly copyError = signal(false);
  readonly dialogKind = signal<SettingsDialog>(null);
  readonly dialogElement = viewChild<ElementRef<HTMLDialogElement>>('dialog');
  readonly busy = computed(() => this.store.busyIds().includes('settings'));
  readonly settingsMutationError = computed(() =>
    this.store.mutationErrorKey() === 'settings'
      ? this.store.mutationError()
      : null
  );
  readonly settings = this.store.settings;
  readonly websiteNumberBusy = computed(() =>
    this.store.busyIds().includes('website-number')
  );
  readonly websiteNumberError = computed(() =>
    this.store.mutationErrorKey() === 'website-number'
      ? this.store.mutationError()
      : null
  );
  /**
   * With no practice phone the website has nothing to fall back to, so switching the voicemail
   * number off is not offered. Switching it back on always is.
   */
  private readonly practicePhoneMissing = computed(
    () => !this.settings()?.practicePhone
  );
  readonly websiteNumberLocked = computed(
    () =>
      this.websiteNumberBusy() ||
      (this.practicePhoneMissing() &&
        this.settings()?.useVoicemailNumberOnWebsite === true)
  );
  readonly websiteNumberHelp = computed(() => {
    if (this.settings()?.useVoicemailNumberOnWebsite === false)
      return 'Your website shows your practice phone. Voicemail stays on for calls to this number.';
    return this.practicePhoneMissing()
      ? 'Your website shows this number. Add a practice phone number in Account settings to show that instead.'
      : 'Your website shows this number. Turn off to show your practice phone instead.';
  });
  /** Either read failing is shown here; the settings-only refresh has its own channel. */
  readonly loadError = computed(
    () => this.store.settingsError() ?? this.store.error()
  );
  readonly refreshing = computed(
    () => this.store.loading() || this.store.settingsRefreshing()
  );
  readonly state = computed(() => this.settings()?.state ?? 'not-configured');
  readonly active = computed(() => this.state() === 'active');
  readonly unresolved = computed(() =>
    ['provisioning', 'checking', 'support-required'].includes(this.state())
  );
  readonly smsAvailable = computed(
    () => this.settings()?.smsAvailable === true
  );
  /** Derived from the Scheduling type by the API; never entered here. */
  readonly bookingLink = computed(
    () => this.settings()?.bookingLink ?? NO_LINK
  );
  /** The override replaces the derived link only while the switch is on and the URL is usable. */
  readonly overrideActive = computed(
    () =>
      this.draftOverrideEnabled() &&
      !!this.draftOverrideUrl().trim() &&
      this.form.controls.overrideUrl.valid
  );
  readonly destination = computed(() =>
    this.overrideActive()
      ? this.draftOverrideUrl().trim()
      : this.bookingLink().url
  );
  /** Callers hear the text-link prompt only when a text could actually be sent. */
  readonly promptOffered = computed(
    () => this.smsAvailable() && !!this.destination()
  );
  readonly includeSmsOffer = computed(
    () => this.previewCall() === 'standard' && this.promptOffered()
  );
  readonly sequenceLabel = computed(() =>
    this.includeSmsOffer()
      ? 'Greeting → Required notice → Text-link prompt → Tone'
      : 'Greeting → Required notice → Tone'
  );
  readonly sourceLabel = computed(() => {
    const source = this.bookingLink().source;
    return source
      ? SOURCE_LABEL[source]
      : 'Your published iCareDx landing page';
  });
  /**
   * Why nothing resolves. One message rather than one per Scheduling type: the API reports that no
   * destination resolved without saying which of the three reasons caused it, so naming any one of
   * them would be a guess, and telling a provider to publish a page they already published is worse
   * than telling them where to look.
   */
  readonly missingNotice = computed(() =>
    this.bookingLink().url
      ? null
      : 'No booking link yet. Under Integrations, choose a scheduling type and make sure your landing page is published and any link you entered is a full HTTPS address — or add a different link for texts below. Until then callers are not offered a text.'
  );
  /**
   * A method, not a computed: it reads the control's touched flag, which changes on blur without
   * any signal write, so a memoised value would miss the moment the error should appear.
   */
  overrideError(): string | null {
    if (!this.draftOverrideEnabled()) return null;
    const control = this.form.controls.overrideUrl;
    if (!control.touched && !this.submitted()) return null;
    if (!this.draftOverrideUrl().trim())
      return 'Enter a full HTTPS link, or switch the override off.';
    return control.invalid ? 'Enter a full HTTPS link.' : null;
  }
  readonly statusLabel = computed(() => {
    switch (this.state()) {
      case 'active':
        return 'Active';
      case 'provisioning':
        return 'Activating';
      case 'checking':
        return 'Checking activation';
      case 'failed':
        return 'Activation failed';
      case 'support-required':
        return 'Support required';
      default:
        return 'Setup';
    }
  });
  readonly subtitle = computed(() => {
    if (this.active())
      return 'Manage your voicemail line, greeting and automatic follow-up texts.';
    return 'Configure voicemail and activate a dedicated iCareDx voicemail line.';
  });
  /** One line for the activation summary: where booking texts will send callers. */
  readonly bookingSummary = computed(() => {
    if (this.overrideActive())
      return this.displayUrl(this.draftOverrideUrl().trim());
    const { url, source } = this.bookingLink();
    if (!url) return 'None';
    return source === 'scheduling-link'
      ? this.displayUrl(url)
      : 'iCareDx landing page';
  });
  private lastSettings: VmSettings | null = null;
  private restoreFocus: HTMLElement | null = null;
  private copyTimer: ReturnType<typeof setTimeout> | null = null;
  private leaveResolver: ((leave: boolean) => void) | null = null;

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.syncDraft();
      this.saved.set(false);
    });
    effect(() => {
      const settings = this.settings();
      if (!settings) return;
      if (!this.lastSettings || !this.form.dirty) this.applySettings(settings);
      this.lastSettings = settings;
    });
    effect(onCleanup => {
      if (!this.dirty()) return;
      const warnBeforeUnload = (event: BeforeUnloadEvent): void => {
        if (!this.form.dirty) return;
        event.preventDefault();
        event.returnValue = '';
      };
      window.addEventListener('beforeunload', warnBeforeUnload);
      // Angular destroys the effect with this component, removing the listener.
      onCleanup(() =>
        window.removeEventListener('beforeunload', warnBeforeUnload)
      );
    });
  }

  displayUrl(url: string): string {
    return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  }

  onInput(): void {
    this.dirty.set(this.form.dirty);
  }

  onOverrideToggle(): void {
    this.dirty.set(this.form.dirty);
    if (!this.form.controls.overrideEnabled.value) return;
    requestAnimationFrame(() =>
      document.getElementById('voicemail-override-url')?.focus()
    );
  }

  async save(): Promise<boolean> {
    if (!this.validate()) return false;
    const successful = await this.store.updateSettings({
      greeting: this.form.controls.greeting.value.trim(),
      overrideUrl: this.form.controls.overrideEnabled.value
        ? this.form.controls.overrideUrl.value.trim()
        : '',
    });
    if (successful) {
      const settings = this.settings();
      if (settings) this.applySettings(settings);
      this.saved.set(true);
    }
    return successful;
  }

  /**
   * Saved as soon as the switch is flipped. The switch is then set from the stored choice either
   * way, so a failed save puts it back where it was rather than leaving it showing a number the
   * website does not.
   */
  async setWebsiteNumber(input: HTMLInputElement): Promise<void> {
    await this.store.setWebsiteNumber(input.checked);
    input.checked = this.settings()?.useVoicemailNumberOnWebsite ?? true;
  }

  requestActivation(): void {
    if (!this.validate()) return;
    this.openDialog('activate');
  }

  async confirmActivation(): Promise<void> {
    this.closeDialog();
    this.preview.stop();
    if (await this.save()) {
      this.saved.set(false);
      await this.store.activate();
    }
  }

  /**
   * Ask the carrier what became of an activation whose outcome was never seen.
   *
   * An explicit action, not a side effect of reading the screen: it can bind a number and put the
   * line live, which is not something opening a tab should do.
   */
  async recheck(): Promise<void> {
    await this.store.checkActivationStatus();
  }

  /** Settings → Integrations, where the Scheduling type lives. Unsaved edits are guarded first. */
  async openIntegrations(): Promise<void> {
    if (!(await this.requestLeave())) return;
    await this.router.navigate(['/settings'], {
      queryParams: { tab: 'integrations' },
    });
  }

  async openProviderWebsite(): Promise<void> {
    if (!(await this.requestLeave())) return;
    await this.router.navigate(['/provider-website']);
  }

  togglePreview(): void {
    if (
      this.preview.state() === 'loading' ||
      this.preview.state() === 'playing'
    ) {
      this.preview.stop();
      return;
    }
    this.form.controls.greeting.markAsTouched();
    if (this.form.controls.greeting.invalid) return;
    // What is on screen, saved or not. The API decides from it whether the text-link prompt plays,
    // by the same rules as a call.
    void this.preview.play({
      greeting: this.form.controls.greeting.value,
      call: this.previewCall(),
      bookingLinkOverride: this.overrideActive()
        ? this.draftOverrideUrl().trim()
        : null,
    });
  }

  selectPreviewCall(call: 'standard' | 'booked'): void {
    this.preview.stop();
    this.previewCall.set(call);
  }

  async copyNumber(): Promise<void> {
    const phoneNumber = this.settings()?.phoneNumber;
    if (!phoneNumber) return;
    this.copyError.set(false);
    try {
      await navigator.clipboard.writeText(phoneNumber);
      this.copied.set(true);
      if (this.copyTimer) clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => this.copied.set(false), 2400);
    } catch {
      this.copyError.set(true);
    }
  }

  async leave(): Promise<void> {
    if (await this.requestLeave()) this.back.emit();
  }

  requestLeave(): Promise<boolean> {
    if (this.busy() && !this.unresolved()) return Promise.resolve(false);
    if (!this.form.dirty) {
      this.preview.stop();
      return Promise.resolve(true);
    }
    this.leaveResolver?.(false);
    this.openDialog('discard');
    return new Promise(resolve => {
      this.leaveResolver = resolve;
    });
  }

  discard(): void {
    const resolve = this.leaveResolver;
    this.leaveResolver = null;
    this.closeDialog();
    this.preview.stop();
    const settings = this.settings();
    if (settings) this.applySettings(settings);
    resolve?.(true);
  }

  openDialog(kind: SettingsDialog): void {
    this.restoreFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    this.dialogKind.set(kind);
    requestAnimationFrame(() => {
      const dialog = this.dialogElement()?.nativeElement;
      if (dialog?.isConnected && this.dialogKind() && !dialog.open)
        dialog.showModal();
    });
  }

  /** Swap the content of the open dialog, e.g. from the preview to the required notices. */
  switchDialog(kind: SettingsDialog): void {
    this.preview.stop();
    this.dialogKind.set(kind);
  }

  closeDialog(): void {
    this.preview.stop();
    this.dialogElement()?.nativeElement.close();
    this.dialogKind.set(null);
    this.restoreFocus?.focus();
    this.leaveResolver?.(false);
    this.leaveResolver = null;
  }

  onDialogCancel(event: Event): void {
    event.preventDefault();
    this.closeDialog();
  }

  ngOnDestroy(): void {
    this.preview.stop();
    if (this.copyTimer) clearTimeout(this.copyTimer);
    this.leaveResolver?.(false);
    this.dialogElement()?.nativeElement.close();
  }

  private validate(): boolean {
    this.submitted.set(true);
    this.form.markAllAsTouched();
    this.syncDraft();
    if (this.form.controls.greeting.invalid) {
      document.getElementById('voicemail-greeting')?.focus();
      return false;
    }
    if (this.overrideError()) {
      document.getElementById('voicemail-override-url')?.focus();
      return false;
    }
    return true;
  }

  private applySettings(settings: VmSettings): void {
    this.form.reset(
      {
        greeting: settings.greeting,
        overrideEnabled: !!settings.overrideUrl,
        overrideUrl: settings.overrideUrl,
      },
      { emitEvent: false }
    );
    this.syncDraft();
    this.submitted.set(false);
  }

  private syncDraft(): void {
    this.draftGreeting.set(this.form.controls.greeting.value);
    this.draftOverrideEnabled.set(this.form.controls.overrideEnabled.value);
    this.draftOverrideUrl.set(this.form.controls.overrideUrl.value);
    this.dirty.set(this.form.dirty);
  }
}
